import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Trash2, Users as UsersIcon, Settings } from 'lucide-react';
import { format } from 'date-fns';

export default function UserManagement({ user }) {
  const [managers, setManagers] = useState([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isPermissionDialogOpen, setIsPermissionDialogOpen] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [permissions, setPermissions] = useState({
    can_view_income: true,
    can_edit_income: false,
    can_view_expenses: true,
    can_edit_expenses: false,
  });

  useEffect(() => {
    fetchManagers();
  }, []);

  const fetchManagers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setManagers(response.data);
    } catch (error) {
      toast.error('Failed to fetch managers');
    }
  };

  const handleCreateManager = async (e) => {
    e.preventDefault();

    try {
      await axios.post(`${API}/users`, {
        ...formData,
        role: 'manager',
        permissions: permissions,
      });
      toast.success('Manager created successfully');
      setIsCreateDialogOpen(false);
      resetForm();
      fetchManagers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create manager');
    }
  };

  const handleDeleteManager = async (managerId) => {
    if (!window.confirm('Are you sure you want to delete this manager?')) return;

    try {
      await axios.delete(`${API}/users/${managerId}`);
      toast.success('Manager deleted successfully');
      fetchManagers();
    } catch (error) {
      toast.error('Failed to delete manager');
    }
  };

  const handleUpdatePermissions = async () => {
    if (!selectedManager) return;

    try {
      await axios.put(`${API}/users/${selectedManager.id}`, permissions);
      toast.success('Permissions updated successfully');
      setIsPermissionDialogOpen(false);
      setSelectedManager(null);
      fetchManagers();
    } catch (error) {
      toast.error('Failed to update permissions');
    }
  };

  const openPermissionDialog = (manager) => {
    setSelectedManager(manager);
    setPermissions(manager.permissions || {
      can_view_income: true,
      can_edit_income: false,
      can_view_expenses: true,
      can_edit_expenses: false,
    });
    setIsPermissionDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({ email: '', password: '' });
    setPermissions({
      can_view_income: true,
      can_edit_income: false,
      can_view_expenses: true,
      can_edit_expenses: false,
    });
  };

  const canAddManager = managers.length < 2;

  return (
    <div className="space-y-6" data-testid="user-management-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">User Management</h2>
          <p className="text-slate-600 mt-1">Manage managers and their permissions</p>
        </div>
        {canAddManager && (
          <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
            setIsCreateDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700" data-testid="add-manager-button">
                <Plus className="w-4 h-4" />
                Add Manager
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="create-manager-dialog">
              <DialogHeader>
                <DialogTitle>Create New Manager</DialogTitle>
                <DialogDescription>Add a new manager account with custom permissions</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateManager} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    data-testid="manager-email-input"
                    type="email"
                    placeholder="manager@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    data-testid="manager-password-input"
                    type="password"
                    placeholder="Enter password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-4 pt-4 border-t">
                  <h4 className="font-medium text-slate-900">Permissions</h4>
                  
                  <div className="flex items-center justify-between">
                    <Label htmlFor="view-income">View Income</Label>
                    <Switch
                      id="view-income"
                      data-testid="permission-view-income"
                      checked={permissions.can_view_income}
                      onCheckedChange={(checked) => setPermissions({ ...permissions, can_view_income: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label htmlFor="edit-income">Edit Income</Label>
                    <Switch
                      id="edit-income"
                      data-testid="permission-edit-income"
                      checked={permissions.can_edit_income}
                      onCheckedChange={(checked) => setPermissions({ ...permissions, can_edit_income: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label htmlFor="view-expenses">View Expenses</Label>
                    <Switch
                      id="view-expenses"
                      data-testid="permission-view-expenses"
                      checked={permissions.can_view_expenses}
                      onCheckedChange={(checked) => setPermissions({ ...permissions, can_view_expenses: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label htmlFor="edit-expenses">Edit Expenses</Label>
                    <Switch
                      id="edit-expenses"
                      data-testid="permission-edit-expenses"
                      checked={permissions.can_edit_expenses}
                      onCheckedChange={(checked) => setPermissions({ ...permissions, can_edit_expenses: checked })}
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full" data-testid="create-manager-submit">
                  Create Manager
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {!canAddManager && (
        <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
          <p className="text-sm text-amber-800">
            Maximum of 2 managers reached. Delete an existing manager to add a new one.
          </p>
        </div>
      )}

      <Card className="border-0 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
          <div className="flex items-center gap-3">
            <UsersIcon className="w-6 h-6 text-blue-600" />
            <div>
              <CardTitle>Managers ({managers.length}/2)</CardTitle>
              <CardDescription>View and manage manager accounts</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {/* Mobile Card View */}
          <div className="md:hidden">
            {managers.length === 0 ? (
              <div className="text-center py-8 px-4 text-slate-500">
                No managers yet. Click "Add Manager" to create one.
              </div>
            ) : (
              <div className="space-y-3 p-4">
                {managers.map((manager) => (
                  <Card key={manager.id} className="border-l-4 border-l-blue-500" data-testid={`manager-row-${manager.id}`}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-slate-900">{manager.email}</div>
                          <div className="text-xs text-slate-500">
                            Created {format(new Date(manager.created_at), 'MMM dd, yyyy')}
                          </div>
                        </div>
                      </div>
                      
                      <div className="mb-3">
                        <div className="text-xs font-medium text-slate-600 mb-2">Permissions:</div>
                        <div className="flex flex-wrap gap-1">
                          {manager.permissions?.can_edit_income && (
                            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">Edit Income</span>
                          )}
                          {manager.permissions?.can_view_income && !manager.permissions?.can_edit_income && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">View Income</span>
                          )}
                          {manager.permissions?.can_edit_expenses && (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Edit Expenses</span>
                          )}
                          {manager.permissions?.can_view_expenses && !manager.permissions?.can_edit_expenses && (
                            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">View Expenses</span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 pt-3 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openPermissionDialog(manager)}
                          data-testid={`edit-permissions-${manager.id}`}
                          className="flex-1"
                        >
                          <Settings className="w-4 h-4 mr-1" />
                          Edit Permissions
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteManager(manager.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-manager-${manager.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Permissions</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {managers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                      No managers yet. Click "Add Manager" to create one.
                    </TableCell>
                  </TableRow>
                ) : (
                  managers.map((manager) => (
                    <TableRow key={manager.id} data-testid={`manager-row-${manager.id}`}>
                      <TableCell className="font-medium">{manager.email}</TableCell>
                      <TableCell>{format(new Date(manager.created_at), 'MMM dd, yyyy')}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {manager.permissions?.can_edit_income && (
                            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">Edit Income</span>
                          )}
                          {manager.permissions?.can_view_income && !manager.permissions?.can_edit_income && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">View Income</span>
                          )}
                          {manager.permissions?.can_edit_expenses && (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Edit Expenses</span>
                          )}
                          {manager.permissions?.can_view_expenses && !manager.permissions?.can_edit_expenses && (
                            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">View Expenses</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openPermissionDialog(manager)}
                            data-testid={`edit-permissions-${manager.id}`}
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteManager(manager.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-manager-${manager.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Permission Edit Dialog */}
      <Dialog open={isPermissionDialogOpen} onOpenChange={setIsPermissionDialogOpen}>
        <DialogContent data-testid="edit-permissions-dialog">
          <DialogHeader>
            <DialogTitle>Edit Permissions</DialogTitle>
            <DialogDescription>
              Update permissions for {selectedManager?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="edit-view-income">View Income</Label>
              <Switch
                id="edit-view-income"
                data-testid="edit-permission-view-income"
                checked={permissions.can_view_income}
                onCheckedChange={(checked) => setPermissions({ ...permissions, can_view_income: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="edit-edit-income">Edit Income</Label>
              <Switch
                id="edit-edit-income"
                data-testid="edit-permission-edit-income"
                checked={permissions.can_edit_income}
                onCheckedChange={(checked) => setPermissions({ ...permissions, can_edit_income: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="edit-view-expenses">View Expenses</Label>
              <Switch
                id="edit-view-expenses"
                data-testid="edit-permission-view-expenses"
                checked={permissions.can_view_expenses}
                onCheckedChange={(checked) => setPermissions({ ...permissions, can_view_expenses: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="edit-edit-expenses">Edit Expenses</Label>
              <Switch
                id="edit-edit-expenses"
                data-testid="edit-permission-edit-expenses"
                checked={permissions.can_edit_expenses}
                onCheckedChange={(checked) => setPermissions({ ...permissions, can_edit_expenses: checked })}
              />
            </div>

            <Button onClick={handleUpdatePermissions} className="w-full" data-testid="update-permissions-submit">
              Update Permissions
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
